import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "../Component/CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";

const FREE_SHIPPING_MIN = 75;

function formatMoney(n) {
  const v = Number.parseFloat(String(n), 10);
  if (Number.isNaN(v)) return "£0";
  return `£${Math.round(v)}`;
}

function parseProductPrice(priceStr) {
  const n = Number.parseFloat(String(priceStr).replace(/,/g, ""), 10);
  return Number.isNaN(n) ? 0 : n;
}

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [giftWrap, setGiftWrap] = useState(false);

  const { subtotal, lineRows } = useMemo(() => {
    let sum = 0;
    const rows = [];
    cartItems.forEach((item) => {
      const product = productData.find((p) => p.id === item.id);
      if (!product) return;
      const unit = parseProductPrice(product.price);
      const line = unit * item.quantity;
      sum += line;
      rows.push({ item, product, unit, line });
    });
    return { subtotal: sum, lineRows: rows };
  }, [cartItems]);

  const giftWrapPrice = 10;
  const orderTotal = subtotal + (giftWrap ? giftWrapPrice : 0);
  const qualifiesFreeShipping = subtotal >= FREE_SHIPPING_MIN;
  const progressPct = Math.min(
    100,
    subtotal <= 0 ? 0 : (subtotal / FREE_SHIPPING_MIN) * 100
  );
  const amountToFree = Math.max(0, FREE_SHIPPING_MIN - subtotal);

  const handleUpdateQuantity = (id, size, color, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(id, size, color);
    } else {
      updateQuantity(id, size, color, newQuantity);
    }
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      navigate("/checkout");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <Header />
        <main className="cart-page__main" role="main">
          <div className="cart-page__empty">
            <h1 className="cart-page__title">Your bag</h1>
            <p className="cart-page__empty-text">Your bag is empty.</p>
            <Link className="cart-page__shop-link" to="/shop" onClick={toTop}>
              Continue shopping
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Header />
      <main className="cart-page__main" role="main" id="MainContent">
        <div className="cart-page__container">
          <header className="cart-page__head">
            <div className="cart-page__head-text">
              <h1 className="cart-page__title">Your bag</h1>
              <p className="cart-page__shipping-msg">
                {qualifiesFreeShipping
                  ? "Congratulations! You're getting free shipping"
                  : `Add ${formatMoney(
                      amountToFree
                    )} more for free shipping on £${FREE_SHIPPING_MIN}+ orders.`}
              </p>
            </div>
            <div className="cart-page__progress" aria-hidden="true">
              <div className="cart-page__progress-track">
                <div
                  className="cart-page__progress-fill"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </header>

          <div className="cart-page__rule" />

          <ul className="cart-page__lines" aria-label="Cart items">
            {lineRows.map(({ item, product, unit, line }) => (
              <li
                key={`${item.id}-${item.size}-${item.color}`}
                className="cart-page__line"
              >
                <Link
                  className="cart-page__line-img-wrap"
                  to={`/shopDetails/${product.id}`}
                  onClick={toTop}
                >
                  <img
                    src={product.image}
                    alt=""
                    className="cart-page__line-img"
                  />
                </Link>
                <div className="cart-page__line-body">
                  <Link
                    className="cart-page__line-name"
                    to={`/shopDetails/${product.id}`}
                    onClick={toTop}
                  >
                    {product.name}
                  </Link>
                  <p className="cart-page__line-meta">
                    {item.color} / {item.size}
                  </p>
                  <div className="cart-page__line-actions">
                    <div className="cart-page__qty">
                      <button
                        type="button"
                        className="cart-page__qty-btn"
                        aria-label="Decrease quantity"
                        onClick={() =>
                          handleUpdateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity - 1
                          )
                        }
                      >
                        −
                      </button>
                      <span className="cart-page__qty-val">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        className="cart-page__qty-btn"
                        aria-label="Increase quantity"
                        onClick={() =>
                          handleUpdateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="cart-page__remove"
                      onClick={() =>
                        removeFromCart(item.id, item.size, item.color)
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div className="cart-page__line-price">
                  <span className="cart-page__line-unit">
                    {formatMoney(unit)} each
                  </span>
                  <span className="cart-page__line-total">
                    {formatMoney(line)}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          <div className="cart-page__rule" />

          <div className="cart-page__summary-wrap">
            <div className="cart-page__summary">
              <dl className="cart-page__summary-rows">
                <div className="cart-page__summary-row">
                  <dt>Discounts</dt>
                  <dd>Applied at checkout</dd>
                </div>
                <div className="cart-page__summary-row">
                  <dt>Shipping</dt>
                  <dd>
                    {qualifiesFreeShipping
                      ? "Free shipping"
                      : "Calculated at checkout"}
                  </dd>
                </div>
                <div className="cart-page__summary-row">
                  <dt>Taxes</dt>
                  <dd>Calculated at checkout</dd>
                </div>
              </dl>
              <div className="cart-page__subtotal-row">
                <span className="cart-page__subtotal-label">Subtotal</span>
                <span className="cart-page__subtotal-value">
                  {formatMoney(orderTotal)}
                </span>
              </div>
              <button
                type="button"
                className="cart-page__checkout"
                onClick={handleCheckout}
              >
                Checkout
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
